import { Button, ButtonGroup } from "@blueprintjs/core";
import React, { useCallback } from "react";
import {
    calculateZoomDomain,
    canZoomIn as canZoomInHelper,
    canZoomOut as canZoomOutHelper,
    DEFAULT_ZOOM_STEP
} from "./graphHelpers";

type ZoomControlsProps = {
    readonly xDomain: [number, number] | null;
    readonly onXDomainChange: (domain: [number, number] | null) => void;
    readonly dataBounds: { min: number; max: number };
    readonly zoomStep?: number;
};

export const ZoomControls: React.FC<ZoomControlsProps> = ({
    xDomain,
    onXDomainChange,
    dataBounds,
    zoomStep = DEFAULT_ZOOM_STEP
}) => {
    const canZoomIn = canZoomInHelper(xDomain, dataBounds);
    const canZoomOut = canZoomOutHelper(xDomain, dataBounds);

    const applyZoom = useCallback(
        (factor: number) => {
            const newDomain = calculateZoomDomain(xDomain, dataBounds, factor);
            onXDomainChange(newDomain);
        },
        [xDomain, dataBounds, onXDomainChange]
    );

    const handleZoomIn = useCallback(() => {
        applyZoom(1 / zoomStep);
    }, [applyZoom, zoomStep]);

    const handleZoomOut = useCallback(() => {
        applyZoom(zoomStep);
    }, [applyZoom, zoomStep]);

    return (
        <ButtonGroup minimal>
            <Button
                size="small"
                variant="minimal"
                icon="zoom-in"
                onClick={handleZoomIn}
                disabled={!canZoomIn}
                title="Zoom in"
            />
            <Button
                size="small"
                variant="minimal"
                icon="zoom-out"
                onClick={handleZoomOut}
                disabled={!canZoomOut}
                title="Zoom out"
            />
        </ButtonGroup>
    );
};
